import { useEffect, useMemo, useState } from "react";
import { Search, Fish, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/StatCard";
import { FishCondition } from "@/components/FishCondition";
import { GradeBadge } from "@/components/GradeSelect";

const fmtQty = (n) => Number(n).toLocaleString("id-ID", { maximumFractionDigits: 2 });

export default function StockView() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/stock/available").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const groups = useMemo(() => {
    const f = (rows || []).filter((r) => `${r.species} ${r.size} ${r.grade || ""}`.toLowerCase().includes(q.toLowerCase()));
    const m = {};
    f.forEach((r) => { (m[r.species] = m[r.species] || []).push(r); });
    return Object.entries(m);
  }, [rows, q]);

  return (
    <div className="space-y-6" data-testid="stock-view-page">
      <PageHeader title="Stok Tersedia" subtitle="Jenis ikan dan jumlah yang tersedia saat ini di cold storage" />
      {user?.approval_pending && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="visitor-pending-banner">
          <Clock className="h-4 w-4 mt-0.5 shrink-0" />
          <p>Anda masuk sebagai <b>Pengunjung</b>. Akun Anda menunggu persetujuan Pemilik/Admin. Setelah disetujui, menu lain akan muncul sesuai role Anda.</p>
        </div>
      )}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari jenis, size, atau grade" className="pl-9 bg-white" data-testid="input-stock-view-search" />
      </div>
      {rows === null ? <p className="text-slate-500 text-sm">Memuat stok…</p> : groups.length === 0 ? (
        <div className="card-aqua p-8 text-center text-slate-500" data-testid="stock-view-empty">Belum ada stok yang tersedia.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groups.map(([sp, items], gi) => {
            const totals = items.reduce((a, r) => ({ ...a, [r.unit]: (a[r.unit] || 0) + r.quantity }), {});
            return (
              <div key={sp} className="card-aqua p-4 rise" style={{ animationDelay: `${gi * 40}ms` }} data-testid={`stock-view-species-${gi}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900"><Fish className="h-4 w-4 text-sky-500" />{sp}</div>
                  <div className="font-mono text-sm font-bold text-sky-800" data-testid={`stock-view-total-${gi}`}>{Object.entries(totals).map(([u, v]) => `${fmtQty(v)} ${u}`).join(" + ")}</div>
                </div>
                <ul className="mt-3 divide-y divide-sky-50">
                  {items.map((r, i) => (
                    <li key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`stock-view-row-${gi}-${i}`}>
                      <span className="text-slate-700">{r.size}<GradeBadge value={r.grade} /><FishCondition value={r.condition} /></span>
                      <span className="font-mono font-semibold text-slate-900">{fmtQty(r.quantity)} {r.unit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
